import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './store/AuthContext';
import { Toaster } from 'react-hot-toast';

// Layout Components
import Layout from './components/layout/Layout';
import AuthLayout from './components/layout/AuthLayout';

// Critical components loaded immediately
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import OnboardingPage from './pages/auth/OnboardingPage';
import LoadingSpinner from './components/ui/LoadingSpinner';
import NotFoundPage from './pages/NotFoundPage';

// Lazy load non-critical pages for better performance
const HomePage = lazy(() => import('./pages/HomePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const SwipePage = lazy(() => import('./pages/SwipePage'));
const MatchesPage = lazy(() => import('./pages/MatchesPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const InterestedTradespeopleePage = lazy(() => import('./pages/InterestedTradespeopleePage'));
// const HybridChatDemo = lazy(() => import('./pages/HybridChatDemo')); // Removed since it's now integrated into MessagesPage

// Enhanced loading component with context
const PageLoadingFallback: React.FC<{ pageName?: string }> = ({ pageName }) => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <div className="text-center">
      <LoadingSpinner />
      <p className="mt-4 text-sm text-gray-600">
        {pageName ? `Loading ${pageName}...` : 'Loading...'}
      </p>
    </div>
  </div>
);

// Enhanced error boundary for route errors
class RouteErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; fallback?: React.ComponentType }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Route Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || NotFoundPage;
      return <FallbackComponent />;
    }

    return this.props.children;
  }
}

// Role-based route guard
const RoleGuard: React.FC<{ 
  children: React.ReactNode; 
  allowedRoles: string[];
  fallbackPath?: string;
}> = ({ children, allowedRoles, fallbackPath = '/dashboard' }) => {
  const { user } = useAuth();
  
  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to={fallbackPath} replace />;
  }
  
  return <>{children}</>;
};

// Enhanced Protected Route Component with better error handling
const ProtectedRoute: React.FC<{ 
  children: React.ReactNode;
  requireOnboarding?: boolean;
  allowedRoles?: string[];
}> = ({ children, requireOnboarding = true, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoadingFallback pageName="authentication" />;
  }

  if (!user) {
    // Store the attempted path for redirect after login
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user needs to complete onboarding
  if (requireOnboarding && !user.isOnboarded) {
    return <Navigate to="/onboarding" replace />;
  }

  // Check role permissions if specified
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
};

// Enhanced Public Route Component with redirect logic
const PublicRoute: React.FC<{ 
  children: React.ReactNode;
  redirectOnAuth?: string;
}> = ({ children, redirectOnAuth = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <PageLoadingFallback pageName="authentication" />;
  }

  if (user) {
    // If user is not onboarded, redirect to onboarding
    if (!user.isOnboarded) {
      return <Navigate to="/onboarding" replace />;
    }
    
    // Check if there's a redirect path from login attempt
    const from = (location.state as any)?.from?.pathname;
    if (from && from !== location.pathname) {
      return <Navigate to={from} replace />;
    }
    
    return <Navigate to={redirectOnAuth} replace />;
  }

  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
};

// Semi-protected route for onboarding (user exists but may not be onboarded)
const OnboardingRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageLoadingFallback pageName="onboarding" />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.isOnboarded) {
    return <Navigate to="/dashboard" replace />;
  }

  return <RouteErrorBoundary>{children}</RouteErrorBoundary>;
};

// Main App Component
const App: React.FC = () => {
  return (
    <div className="App">
      {/* Global toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 5000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />

      <Routes>
        {/* Public Routes */}
        <Route 
          path="/" 
          element={
            <PublicRoute>
              <LandingPage />
            </PublicRoute>
          } 
        />
        
        {/* Authentication Routes */}
        <Route 
          path="/login" 
          element={
            <PublicRoute>
              <AuthLayout>
                <LoginPage />
              </AuthLayout>
            </PublicRoute>
          } 
        />
        
        <Route 
          path="/register" 
          element={
            <PublicRoute>
              <AuthLayout>
                <RegisterPage />
              </AuthLayout>
            </PublicRoute>
          } 
        />
        
        {/* Onboarding Route (Semi-protected) */}
        <Route 
          path="/onboarding" 
          element={
            <OnboardingRoute>
              <AuthLayout>
                <OnboardingPage />
              </AuthLayout>
            </OnboardingRoute>
          } 
        />

        {/* Home Route */}
        <Route 
          path="/home" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Home" />}>
                  <HomePage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />

        {/* Protected Routes with Layout */}
        <Route 
          path="/dashboard" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Dashboard" />}>
                  <DashboardPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Profile" />}>
                  <ProfilePage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/account-settings" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Settings" />}>
                  <AccountSettingsPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/jobs" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Jobs" />}>
                  <JobsPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Tradesperson-only routes */}
        <Route 
          path="/swipe" 
          element={
            <ProtectedRoute allowedRoles={['TRADESPERSON']}>
              <Layout>
                <RoleGuard allowedRoles={['TRADESPERSON']}>
                  <Suspense fallback={<PageLoadingFallback pageName="Swipe" />}>
                    <SwipePage />
                  </Suspense>
                </RoleGuard>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/matches" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Matches" />}>
                  <MatchesPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/messages" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Messages" />}>
                  <MessagesPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Removed HybridChatDemo route since it's now integrated into MessagesPage */}
        {/* <Route 
          path="/hybrid-chat-demo" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Hybrid Chat Demo" />}>
                  <HybridChatDemo />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        /> */}
        
        <Route 
          path="/subscription" 
          element={
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<PageLoadingFallback pageName="Subscription" />}>
                  <SubscriptionPage />
                </Suspense>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        {/* Dynamic routes with parameters */}
        <Route 
          path="/interested" 
          element={
            <ProtectedRoute allowedRoles={['HOMEOWNER']}>
              <Layout>
                <RoleGuard allowedRoles={['HOMEOWNER']}>
                  <Suspense fallback={<PageLoadingFallback pageName="Interested Tradespeople" />}>
                    <InterestedTradespeopleePage />
                  </Suspense>
                </RoleGuard>
              </Layout>
            </ProtectedRoute>
          } 
        />
        
        <Route 
          path="/interested/:jobId" 
          element={
            <ProtectedRoute allowedRoles={['HOMEOWNER']}>
              <Layout>
                <RoleGuard allowedRoles={['HOMEOWNER']}>
                  <Suspense fallback={<PageLoadingFallback pageName="Job Interested Tradespeople" />}>
                    <InterestedTradespeopleePage />
                  </Suspense>
                </RoleGuard>
              </Layout>
            </ProtectedRoute>
          } 
        />

        {/* Catch-all route for 404s */}
        <Route 
          path="*" 
          element={<NotFoundPage />} 
        />
      </Routes>
    </div>
  );
};

export default App;